import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useTalentMarketplace(orgId?: string | null) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["talent_marketplace"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, bio, objective, city, state, profile_status")
        .eq("visibility_global", true)
        .eq("profile_status", "ready")
        .eq("role", "student");
      if (error) throw error;
      return data;
    },
  });

  const getProfilePublic = async (profileId: string) => {
    const [profileRes, expRes, portfolioRes, coursesRes, interestsRes, warmupRes] = await Promise.all([
      supabase.from("profiles").select("id, full_name, avatar_url, bio, objective, city, state, profile_status").eq("id", profileId).eq("visibility_global", true).single(),
      supabase.from("experiences").select("*").eq("user_id", profileId).order("created_at", { ascending: false }),
      supabase.from("portfolio_links").select("*").eq("user_id", profileId),
      supabase.from("completed_courses").select("*").eq("user_id", profileId),
      supabase.from("profile_interests").select("tag_id, interest_tags(id, name, category)").eq("user_id", profileId),
      supabase.from("warmup_responses").select("id, prompt_id, submitted_at, warmup_prompts(title)").eq("user_id", profileId).eq("status", "submitted").order("submitted_at", { ascending: false }),
    ]);

    return {
      profile: profileRes.data,
      experiences: expRes.data ?? [],
      portfolio: portfolioRes.data ?? [],
      courses: coursesRes.data ?? [],
      interests: interestsRes.data ?? [],
      warmups: warmupRes.data ?? [],
    };
  };

  const logAudit = useMutation({
    mutationFn: async ({ action, entityType, entityId, metadata }: { action: string; entityType: string; entityId: string; metadata?: Record<string, unknown> }) => {
      if (!orgId) return;
      const row = {
        org_id: orgId,
        actor_user_id: user!.id,
        action,
        entity_type: entityType,
        entity_id: entityId,
        metadata: (metadata as any) || null,
      };
      const { error } = await supabase.from("partner_audit_logs").insert([row] as any);
      if (error) throw error;
    },
  });

  return {
    talents: query.data ?? [],
    isLoading: query.isLoading,
    getProfilePublic,
    logAudit,
  };
}
